import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import { getAvailableDeliverySlotLabels } from '../../utils/deliverySlots';
import { formatTimeSaudi } from '../../utils/saudiTime';

export default function SalesOrdersHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [slotCounts, setSlotCounts] = useState(null);

  const loadOrders = async () => {
    setError('');
    setLoading(true);
    try {
      const [ordersRes, slotCountsRes] = await Promise.all([
        apiService.orders.getSalesOrdersHistory(),
        apiService.orders.getSalesOrderSlotCountsHistory(),
      ]);
      if (Array.isArray(ordersRes.data)) setOrders(ordersRes.data);
      setSlotCounts(slotCountsRes.data?.countsBySlot || {});
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load orders history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const formatDateTime = formatTimeSaudi;

  const slotOptions = getAvailableDeliverySlotLabels(new Date());
  const slotSummary = (() => {
    const counts = slotCounts || {};
    const orderedSlots = slotOptions.map((s) => ({ label: s, count: Number(counts[s] || 0) }));
    const otherCounts = Object.entries(counts)
      .filter(([k]) => k && !slotOptions.includes(k))
      .map(([k, v]) => ({ label: k, count: Number(v || 0) }));
    const noSlotCount = Number(counts[''] || 0);
    return { orderedSlots, otherCounts, noSlotCount };
  })();

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-2 sm:mb-4">Orders History</h1>
        <p className="text-gray-600 text-sm mb-4 sm:mb-6">All Bike Sales orders.</p>

        {!loading && slotCounts && (
          <div className="mb-5">
            <div className="text-sm font-semibold text-gray-700 mb-2">Slot wise number of orders</div>
            <div className="space-y-2">
              {slotSummary.orderedSlots.map((s) => (
                <div key={s.label} className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3">
                  <span className="font-medium text-gray-800">{s.label}</span>
                  <span className="font-bold text-red-600">{s.count} Orders</span>
                </div>
              ))}
              {slotSummary.otherCounts.map((s) => (
                <div key={s.label} className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 bg-gray-50">
                  <span className="font-medium text-gray-700">{s.label}</span>
                  <span className="font-bold text-gray-700">{s.count} Orders</span>
                </div>
              ))}
              {slotSummary.noSlotCount > 0 && (
                <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 bg-gray-50">
                  <span className="font-medium text-gray-700">No slot selected</span>
                  <span className="font-bold text-gray-700">{slotSummary.noSlotCount} Orders</span>
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        {loading && (
          <div className="py-8 text-center text-gray-500">Loading orders…</div>
        )}

        {!loading && orders.length === 0 && (
          <div className="bg-white rounded-lg p-8 text-center text-gray-600 border border-gray-200 border-l-4 border-l-green-400">
            <p className="mb-2">No orders yet.</p>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <div className="space-y-4">
            {orders.map((order) => {
              const itemCount = (order.items || []).reduce((sum, i) => sum + (i.orderQty || 0), 0);
              return (
                <div
                  key={order._id}
                  className="border border-gray-200 rounded-lg p-3 sm:p-4 border-l-4 border-l-blue-400 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-gray-800 text-base sm:text-lg">{order.orderNo}</div>
                      <div className="text-base text-gray-500 mt-1">
                        {formatDateTime(order.dateTime)} · {itemCount} item(s)
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      <span className="inline-block px-3 py-1.5 rounded-full text-sm font-medium border bg-gray-100 text-gray-800 border-gray-200">
                        {order.status || '—'}
                      </span>
                    </div>
                  </div>

                  {(order.customerName || order.customerPhone || order.deliverySlot) && (
                    <div className="text-base sm:text-lg text-gray-700 mt-2 font-medium">
                      {order.customerName && (
                        <span className="block sm:inline">
                          Customer: <span className="text-gray-900">{order.customerName}</span>
                        </span>
                      )}
                      {order.customerPhone && (
                        <span className="block sm:inline mt-0.5 sm:mt-0 sm:ml-4">
                          Phone: <span className="text-gray-900">{order.customerPhone}</span>
                        </span>
                      )}
                      {order.deliverySlot && (
                        <div className="block sm:inline mt-2 sm:mt-0 sm:ml-4">
                          <span className="text-base sm:text-lg font-semibold text-gray-800">Delivery Slot: </span>
                          <span className="text-base sm:text-lg font-bold text-blue-700">{order.deliverySlot}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {order.items && order.items.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <ul className="space-y-2 text-base text-gray-700">
                        {order.items.map((item, idx) => {
                          const qty = Number(item.orderQty) || 0;
                          const unitPrice = Number(item.unitPrice) || 0;
                          const lineTotal = qty * unitPrice;
                          return (
                            <li key={idx} className="flex justify-between gap-3">
                              <span>
                                {item.itemName} × {qty}
                              </span>
                              <span className="font-medium tabular-nums whitespace-nowrap">
                                {unitPrice > 0 ? `SAR ${lineTotal.toFixed(2)}` : '—'}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      {order.totalAmount != null && Number(order.totalAmount) > 0 && (
                        <div className="flex justify-between text-base font-semibold text-gray-800 mt-3 pt-2 border-t border-gray-100">
                          <span>Total</span>
                          <span className="tabular-nums">SAR {Number(order.totalAmount).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

